import type { QueryResult, OperationVariables } from '@apollo/react-common';
import {
	type QueryLazyOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useLazyQuery,
} from '@apollo/react-hooks';
import { fg } from '@confluence/feature-gating';
import { type GraphQLTaggedNode } from 'react-relay';
import type { ConcreteRequest } from 'relay-runtime';

import { toDocumentNode } from './toDocumentNode.ts';
import type { UseQueryInteropSupportedHookOptions } from './useQueryInterop.ts';

export type UseLazyQueryInteropSupportedOptions<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = UseQueryInteropSupportedHookOptions<TData, TVariables>;

export type UseLazyQueryInteropSupportedReturnType<TData, TVariables> = [
	(options?: QueryLazyOptions<TVariables>) => void,
	UseLazyQueryInteropSupportedResult<TData, TVariables>,
];

export type UseLazyQueryInteropSupportedResult<TData, TVariables> = Pick<
	QueryResult<TData, TVariables>,
	'data' | 'loading' | 'variables' | 'error' | 'called' | 'fetchMore' | 'stopPolling'
>;

export function useLazyQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	query: GraphQLTaggedNode,
	options?: UseLazyQueryInteropSupportedOptions<TData, TVariables>,
): UseLazyQueryInteropSupportedReturnType<TData, TVariables> {
	if (process.env.BUILD_VARIANT === 'relay') {
		throw new Error('[Relay migration] Method not implemented yet');
	} else {
		if (!fg('cc-fd-lazy-query-interop-agg')) {
			return useLazyQuery<TData, TVariables>(toDocumentNode(query), options);
		}

		const { interopApolloAggClient, ...apolloOptions } = options ?? {};
		return useLazyQuery<TData, TVariables>(toDocumentNode(query), {
			...apolloOptions,
			...(interopApolloAggClient ? { client: window['GLOBAL_APOLLO_AGG_CLIENT'] } : {}),
			context: {
				...apolloOptions?.context,
				requestId: (query as ConcreteRequest).params?.id,
			},
		});
	}
}
